import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  collectNodeAndDescendantIds,
  findNodeAndExpandParents,
  getLayersExplorerAllowedNodeTypes,
  LayersContainerContext,
  preserveExpandedState,
  removeNodesById,
  renameNodeInTree,
  UseLayerBroadcastReceiverProps,
} from './utils';
import { TreeService } from 'business/services/TreeService';
import { TreeControlDefaultState, TreeControlState } from 'components/explorer';
import useBroadcast, {
  CHANNEL_ACTIONS,
  CHANNEL_TYPES,
  MessageType,
} from 'business/broadcast';
import {
  EagerLoadTreeNodeDTO,
  FragmentLayerDTO,
  FragmentScreen,
  ScreenDTO,
  TreeNode,
} from 'business/models/Models';
import { GetSeedList, showAlertSnackBar } from 'utils/Global';
import { SeedLayerDTO } from 'business/models/CustomModels';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';

type UseSetSelectedNodeEffectProps = {
  treeControlState: TreeControlState;
  setTreeControlState: React.Dispatch<React.SetStateAction<TreeControlState>>;
  treeNodeId: number;
};

const treeService = new TreeService();
export const useSetSelectedNodeEffect = (props: UseSetSelectedNodeEffectProps) => {
  const { treeControlState, setTreeControlState, treeNodeId } = props;
  const selectionAppliedRef = useRef(false);

  useLayoutEffect(() => {
    if (selectionAppliedRef.current || treeControlState.treenodes.length === 0) {
      return;
    }

    treeService
      .getParentIdByTreeNodeId(treeNodeId)
      .then((parentId) => {
        const { updatedNodes, foundNode } = findNodeAndExpandParents(
          treeControlState.treenodes,
          parentId,
        );

        if (!foundNode) {
          return;
        }

        setTreeControlState((prev) => ({
          ...prev,
          treenodes: updatedNodes,
          selectedExplorerNode: foundNode,
        }));
        selectionAppliedRef.current = true;
      })
      .catch(() => {
        return;
      });
  }, [treeControlState]);
};

const updateNodeNameInArray = (nodes: any[] | undefined, id: number, newName: string) =>
  Array.isArray(nodes)
    ? nodes.map((n) => (n?.id === id ? { ...n, name: newName } : n))
    : nodes;

export const useEagerLoadTreeNodes = () => {
  const [treeControlState, setTreeControlState] = useState<TreeControlState>({
    ...TreeControlDefaultState,
  });
  const isScreenRoute = window.location.pathname.includes('/screen');
  const allowedNodeTypes = getLayersExplorerAllowedNodeTypes(isScreenRoute);
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setTreeControlState((prev) => ({ ...prev, isLoading: true }));
        const data = await treeService.getAllTreeNodesService(
          allowedNodeTypes,
          undefined,
          false,
        );
        setTreeControlState((prev) => ({ ...prev, treenodes: data }));
      } finally {
        setTreeControlState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!explorerBroadcastChannel) {
      return;
    }

    const handleBroadcastMessage = (messageEvent: MessageEvent<MessageType<any>>) => {
      const { action, payload } = messageEvent.data ?? {};

      if (action === CHANNEL_ACTIONS.EXPLORER_DELETE_NODE) {
        const { deletedNodeIds } = (payload ?? {}) as { deletedNodeIds?: number[] };
        if (!deletedNodeIds?.length) {
          return;
        }

        setTreeControlState((prev) => {
          const expandedIds = [
            ...new Set([
              ...deletedNodeIds,
              ...collectNodeAndDescendantIds(prev.treenodes, deletedNodeIds),
            ]),
          ];
          return {
            ...prev,
            treenodes: removeNodesById(prev.treenodes, expandedIds),
            viewNodes: Array.isArray(prev.viewNodes)
              ? prev.viewNodes.filter((n) => !expandedIds.includes(n.id))
              : prev.viewNodes,
            selectedNodes: prev.selectedNodes?.filter((n) => !expandedIds.includes(n.id)),
            selectedExplorerNode: expandedIds.includes(prev.selectedExplorerNode?.id)
              ? null
              : prev.selectedExplorerNode,
          };
        });
        return;
      }

      if (action === CHANNEL_ACTIONS.EXPLORER_RENAME_NODE) {
        const node = payload as { id: number; name: string };
        if (!node?.id) {
          return;
        }

        setTreeControlState((prev) => ({
          ...prev,
          treenodes: renameNodeInTree(prev.treenodes, node.id, node.name),
          viewNodes: updateNodeNameInArray(prev.viewNodes, node.id, node.name),
          selectedNodes: updateNodeNameInArray(prev.selectedNodes, node.id, node.name),
          selectedExplorerNode:
            prev.selectedExplorerNode?.id === node.id
              ? { ...prev.selectedExplorerNode, name: node.name }
              : prev.selectedExplorerNode,
        }));
        return;
      }

      if (
        action === CHANNEL_ACTIONS.EXPLORER_ADD_NODE ||
        action === CHANNEL_ACTIONS.EXPLORER_RESTORE_NODE
      ) {
        // Payload only has parent id(s), not the new nodes. Reload the eager tree
        // so add/restore appear in Layers Explorer and Select Protein ID filters.
        (async () => {
          try {
            const data = await treeService.getAllTreeNodesService(
              allowedNodeTypes,
              undefined,
              false,
            );
            setTreeControlState((prev) => ({
              ...prev,
              treenodes: preserveExpandedState(prev.treenodes, data),
            }));
          } catch {
            return;
          }
        })();
      }
    };

    explorerBroadcastChannel.onmessage = handleBroadcastMessage;
    return () => {
      explorerBroadcastChannel.onmessage = null;
    };
  }, [explorerBroadcastChannel]);

  return { treeControlState, setTreeControlState };
};

export const useLayersContainerContext = () => {
  const context = useContext(LayersContainerContext);
  if (!context) {
    return;
  }
  return context;
};

export const useLayerBroadcastReceiver = (props: UseLayerBroadcastReceiverProps) => {
  const {
    treeNodes,
    selectedScreen,
    setSelectedScreen,
    selectedFragmentLayerDetail,
    setSelectedFragmentLayerDetail,
    editSeedData,
    setEditSeedData,
    setSelectedSeedName,
    selectedProteinLayerDetail,
    setSelectedProteinLayerDetail,
    onFragmentDeleted,
    onProteinDeleted,
    onSeedDeleted,
  } = props;

  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);

  const findScreenTreeNodeId = useCallback(
    (nodes: EagerLoadTreeNodeDTO[], screenId: number): number | undefined => {
      for (const n of nodes) {
        if (n.type === ETreeNodeTypes.Screen && n.extra?.id === screenId) {
          return n.id;
        }
        if (n.children?.length) {
          const found = findScreenTreeNodeId(n.children, screenId);
          if (found !== undefined) {
            return found;
          }
        }
      }
      return undefined;
    },
    [],
  );

  useEffect(() => {
    if (!explorerBroadcastChannel) {
      return;
    }

    const handleBroadcastMessage = (messageEvent: MessageEvent<MessageType<any>>) => {
      const { action, payload } = messageEvent.data;

      if (action === CHANNEL_ACTIONS.EXPLORER_DELETE_NODE) {
        const { deletedNodeIds } = payload as { deletedNodeIds?: number[] };
        if (!deletedNodeIds?.length) {
          return;
        }

        const deletedSet = new Set([
          ...deletedNodeIds,
          ...collectNodeAndDescendantIds(treeNodes, deletedNodeIds),
        ]);
        if (selectedScreen) {
          const screenTreeNodeId = findScreenTreeNodeId(treeNodes, selectedScreen.id);
          if (screenTreeNodeId !== undefined && deletedSet.has(screenTreeNodeId)) {
            setSelectedScreen(undefined);
            showAlertSnackBar(
              EAlertType.WARNING,
              ESnackBarPosition.TOP,
              'Selected screen has been deleted.',
            );
            return;
          }
        }

        if (
          selectedFragmentLayerDetail?.fragmentScreen?.treeNodeId &&
          deletedSet.has(selectedFragmentLayerDetail.fragmentScreen.treeNodeId)
        ) {
          onFragmentDeleted();
          return;
        }

        if (editSeedData?.seedId) {
          const globalSeeds = GetSeedList();
          const matchedSeed = globalSeeds.find((s) => s.id === editSeedData.seedId);
          if (matchedSeed?.treeNodeId && deletedSet.has(matchedSeed.treeNodeId)) {
            onSeedDeleted();
            return;
          }
        }

        const proteinTreeNodeId =
          selectedProteinLayerDetail?.proteinLayerProteinFormulation?.[0]
            ?.proteinFormulation?.id;
        if (proteinTreeNodeId && deletedSet.has(proteinTreeNodeId)) {
          onProteinDeleted();
          return;
        }
      }

      if (action === CHANNEL_ACTIONS.EXPLORER_RENAME_NODE) {
        const node = payload as { id: number; name: string };

        if (selectedScreen) {
          const screenTreeNodeId = findScreenTreeNodeId(treeNodes, selectedScreen.id);
          if (screenTreeNodeId !== undefined && node?.id === screenTreeNodeId) {
            setSelectedScreen((prev) => {
              if (!prev) {
                return prev;
              }
              return new ScreenDTO({ ...prev, name: node.name });
            });
            return;
          }
        }
        if (
          selectedFragmentLayerDetail?.fragmentScreen?.treeNodeId &&
          node?.id === selectedFragmentLayerDetail.fragmentScreen.treeNodeId
        ) {
          setSelectedFragmentLayerDetail((prev) => {
            if (!prev) {
              return prev;
            }
            const updated = Object.assign(new FragmentLayerDTO(), prev);
            if (updated.fragmentScreen) {
              updated.fragmentScreen = Object.assign(
                new FragmentScreen(),
                updated.fragmentScreen,
                {
                  treeNode: updated.fragmentScreen.treeNode
                    ? Object.assign(new TreeNode(), updated.fragmentScreen.treeNode, {
                        name: node.name,
                      })
                    : undefined,
                },
              );
            }
            return updated;
          });
          return;
        }
        if (editSeedData?.seedId) {
          const globalSeeds = GetSeedList();
          const matchedSeed = globalSeeds.find((s) => s.id === editSeedData.seedId);
          if (matchedSeed?.treeNodeId && node?.id === matchedSeed.treeNodeId) {
            setSelectedSeedName(node.name);
            setEditSeedData((prev) => {
              if (!prev) {
                return prev;
              }
              const updated = Object.assign(new SeedLayerDTO(), prev);
              updated.layerName = node.name;
              updated.seedName = node.name;
              return updated;
            });
            return;
          }
        }
      }
    };

    explorerBroadcastChannel.onmessage = handleBroadcastMessage;

    return () => {
      explorerBroadcastChannel.onmessage = null;
    };
  }, [
    explorerBroadcastChannel,
    treeNodes,
    findScreenTreeNodeId,
    selectedScreen,
    selectedFragmentLayerDetail,
    editSeedData,
    selectedProteinLayerDetail,
    setSelectedScreen,
    setSelectedFragmentLayerDetail,
    setEditSeedData,
    setSelectedSeedName,
    setSelectedProteinLayerDetail,
    onFragmentDeleted,
    onProteinDeleted,
    onSeedDeleted,
  ]);
};
